import React, { useContext, useMemo } from 'react';
import ToggleButton, { toggleButtonClasses } from '@mui/material/ToggleButton';
import ToggleButtonGroup, {
  toggleButtonGroupClasses,
} from '@mui/material/ToggleButtonGroup';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import { ThemeContext } from './theme-toggler/theme-context';
import { useHMenuScale } from './appearance';

/* What the strip is built from, at full size. Every one of these is multiplied
   by the scale the setting holds (see appearance.js), so the row keeps
   its proportions whatever size it is asked for - including the width a tab is
   reckoned at when the row is centred, which is what `ww` is for. */
const ww = 130;
const pp = 16;
const BTN_H = 32;
const BTN_MIN = 100;
const TYPE = 14;
const LABEL_TYPE = 18;
const LABEL_MIN = 180;

/* Rounded to a tenth of a pixel: a slider that changes nothing visible for
   three steps in a row feels broken. */
const px = (n, k) => `${Math.round(n * k * 10) / 10}px`;

/* The five application tabs, in order. Exported so a page can title itself from
   the same list the strip is built from, rather than repeating the text and
   letting the two drift. */
export const APP_TAB_ITEMS = [
  "Applications",
  "Traceability",
  "Cloud suitability",
  "Graviton Readiness",
  ".NET Modernization",
];
export const APP_TAB_VALUES = [
  "apps",
  "traceability",
  "assess",
  "graviton",
  "dotnetmodernization",
];
export const appTabTitle = (value) =>
  APP_TAB_ITEMS[APP_TAB_VALUES.indexOf(value)] || "";

const StyledToggleButtonGroup = styled(ToggleButtonGroup, {
  shouldForwardProp: (prop) => !["noScale", "alignleft", "alignLeftSize", "count", "maxWidth", "scale"].includes(prop),
})(({count, noScale,alignleft = false , alignLeftSize=false, maxWidth=false, scale=1}) => ({
  marginTop:"4px",
  position:"absolute",
  height:px(BTN_H, scale),
  minWidth:maxWidth?maxWidth:1822,
  maxHeight:px(BTN_H, scale),
  ...(alignleft == "true" ? {
    transform:noScale?"scale(1)":"scale(0.90)",
    transformOrigin:"left center",
    marginLeft:"12px"} :
    alignLeftSize?
    {
      marginLeft:`max(280px, calc( (50vw - ${alignLeftSize}px) - ${(ww*scale*count)/4}px))`
      
    }:
    {
      marginLeft:count>2?`max(380px, calc( (50vw - 107px) - ${(ww*scale*count)/2}px))`:`max(280px, calc( (50vw - 164px) - ${(ww*scale*count)/4}px))`
    }),
  fontSize:px(TYPE, scale),
  /* The box itself, which MUI gives its own padding: without this the type and
     the widths shrink while the row stays as tall as it ever was, which reads
     as a small menu in a big strip rather than a small strip. */
  [`& .${toggleButtonClasses.root}`]:
    {
      height: px(BTN_H, scale),
      minHeight: px(BTN_H, scale),
      paddingTop: 0,
      paddingBottom: 0,
    },
  [`& .${toggleButtonGroupClasses.firstButton}, & .${toggleButtonGroupClasses.middleButton}`]:
    {
      paddingTop:`${px(pp, scale)} !important`,
      paddingBottom:`${px(pp, scale)} !important`,
      width: `auto`,
      minWidth:px(BTN_MIN, scale),
      fontSize:px(TYPE, scale),
    },
  [`& .${toggleButtonGroupClasses.lastButton}, & .${toggleButtonGroupClasses.middleButton}`]:
    {
      minWidth:px(BTN_MIN, scale),
      paddingTop:`${px(pp, scale)} !important`,
      paddingBottom:`${px(pp, scale)} !important`,
      fontSize:px(TYPE, scale),
      width: `auto`,
    },
  [`& .${toggleButtonGroupClasses.lastButton}.${toggleButtonClasses.disabled}, & .${toggleButtonGroupClasses.middleButton}.${toggleButtonClasses.disabled}`]:
    {
      minWidth:px(BTN_MIN, scale),
      paddingTop:`${px(pp, scale)} !important`,
      paddingBottom:`${px(pp, scale)} !important`,
      width: `auto`,
      fontSize:px(TYPE, scale),
    },
}));

export default function HorizontalMenu({
  isDark,
  /* Lets a page mark its title for styling without reaching in through an
     ancestor selector - the five application tabs use it to share one look. */
  labelClassName="",
  noUnderline=false,
  maxWidth=false,
  noScale=false,
  bgColor,
  textColor="rgba(0,0,0,0.86)",
  alignLeftSize = false,
  alignleft,
  addLabel = true,
  menuCounts =[],
  menuCountStyle = [],
  menuDisabled = [],
  /* A strip of SWITCHES rather than tabs: one `on` per item, so any number of
     them can be lit at once. Left empty - which is every caller but one - the
     strip is a strip of tabs as it always was and the lit one is whichever
     matches `value`. */
  menuActive = [],
  /* What each one says on hover, where there is something worth saying. */
  menuTitles = [],
  children,value, setValue, onChangeValue,
  menuItems = APP_TAB_ITEMS,
  menuValues = APP_TAB_VALUES,
  /* The size the setting asks for, which is what every strip in the product
     uses. A caller passes its own only to show one at a size nobody has
     chosen yet - the preview in Settings, while the slider is moving. */
  scale: scaleProp,
} ) {

  const setScale = useHMenuScale();
  const scale = scaleProp || setScale;
  const { theme } = useContext(ThemeContext);
  const menuTheme = useMemo(() => createTheme({ palette: { mode: isDark ? "dark" : (theme === "dark" ? "dark" : "light") } }), [theme]);

  const mnuMap = {
    [menuValues[0]]:menuItems[0],
    [menuValues[1]]:menuItems[1],
    ...(menuValues[2]?{[menuValues[2]]:menuItems[2]}:{}),
    ...(menuValues[3]?{[menuValues[3]]:menuItems[3]}:{}),
    ...(menuValues[4]?{[menuValues[4]]:menuItems[4]}:{})

  }

const alignmentValues = menuItems.map((m,i) => ({
  [m]: i==0?"left aligned":i==1?"centered":i==2?"right aligned":i==3?"justified":"justified"
}))
  return (
    <ThemeProvider theme={menuTheme}>
    {addLabel && <div
      className={`horizontalMenu ${labelClassName}`}
      style={{
        "--hmenu-k": scale,
        fontSize:px(LABEL_TYPE, scale),
        marginLeft:"10px",
        marginRight:"20px",
        marginTop:"8px",
        minWidth:px(LABEL_MIN, scale),
        ...(maxWidth?{maxWidth:maxWidth}:{})
      }}
      >{mnuMap[value]}</div>
    }
    {children}
    <StyledToggleButtonGroup
      scale={scale}
      maxWidth={maxWidth}
      noScale={noScale}
      alignLeftSize={alignLeftSize}
      alignleft={`${alignleft?alignleft:false}`}
      count={menuItems.length}
      value={value}
      exclusive
      onChange={onChangeValue}
      aria-label="applicationTypeMen"
      /* ...and as a variable, for the parts of the strip that styles.css owns:
         the minimum width of a tab, its side padding, and the rule that marks
         the active one. Those rules fall back to 1, so a `.toggleButton`
         anywhere else is untouched. */
      style={{ "--hmenu-k": scale }}
    >
      {menuItems.map((item, index) => (
        <ToggleButton 
        style={
          {...(bgColor?
            {
              backgroundColor: bgColor,
              color: textColor,
              opacity: 1
            }
            :{})}
        }
        className={`toggleButton ${noUnderline?"noUnder":""} ${(menuActive.length ? menuActive[index] : menuValues[index] == value) ?"active":"" }`}
        key={index}
        value={menuValues[index]}
        title={menuTitles[index] || undefined}
        /* left undefined where nothing was said, so the group goes on working
           out which one is on from its own value */
        selected={menuActive.length ? !!menuActive[index] : undefined}
        aria-label={`${ alignmentValues[item] }`} disabled={!!menuDisabled[index]}>
          <div 
          
          >{item}
            {menuCounts[index]>0 && <div style={{ 
              marginLeft:"6px",
              display:"inline-block",
              background:menuCountStyle[index] == 0 ? "transoarent" : "rgb(var(--color-sorting-tables))",
              color: "white",
              borderRadius:"4px",
              fontFamily: "InterBlack",
              // marginTop:8,
              paddingTop: "0px",
              paddingLeft: "6px",
              paddingRight: "6px",
              transform:"scale(0.7)"
            }}>{menuCounts[index]>0 ? ` ${menuCounts[index]} ` : ""}</div>}
            </div>
        </ToggleButton>
      ))}

    </StyledToggleButtonGroup>
    </ThemeProvider>
  );
}
